// Shared semantic design tokens.
// Keep aliases (primary/secondary/background) for backward compatibility.
const BRAND_PRIMARY = "#00796B";
const BRAND_HOVER = "#00695C";
const BRAND_ACTIVE = "#05554B";
const ACCENT = "#14B8A6";
const ACCENT_SUBTLE = "#E6FFFA";
const ACCENT_BORDER = "#99F6E4";
const LEGACY_PRIMARY = "#2563EB";

const TEXT_PRIMARY = "#0F172A";
const TEXT_SECONDARY = "#334155";
const TEXT_MUTED = "#475569";
const TEXT_SUBTLE = "#94A3B8";

const PAGE_BACKGROUND = "#F7FAFC";
const SURFACE = "#FFFFFF";
const SURFACE_SUBTLE = "#F8FAFC";
const SURFACE_MUTED = "#F1F5F9";

const BORDER_DEFAULT = "#E2E8F0";
const BORDER_STRONG = "#CBD5E1";

const TOPBAR_BACKGROUND = "#07111A";
const TOPBAR_SURFACE = "#111827";
const TOPBAR_SURFACE_HOVER = "#1F2937";
const SIDEBAR_BACKGROUND = "#064E4A";
const SIDEBAR_DARKER = "#042F2E";
const SIDEBAR_ACTIVE = "rgba(20, 184, 166, 0.14)";
const SIDEBAR_ACTIVE_BORDER = "rgba(94, 234, 212, 0.7)";
const CHROME_TEXT = "#CBD5E1";
const CHROME_TEXT_MUTED = "#94A3B8";

const OVERLAY_BACKDROP = "rgba(7, 17, 26, 0.52)";

const DANGER = "#DC2626";
const DANGER_HOVER = "#B91C1C";
const DANGER_ACTIVE = "#991B1B";
const DANGER_SUBTLE = "#FEF2F2";
const DANGER_BORDER = "#FECACA";

const WARNING = "#D97706";
const WARNING_SUBTLE = "#FFF7ED";
const WARNING_BORDER = "#FED7AA";

const SUCCESS = "#059669";
const SUCCESS_SUBTLE = "#ECFDF5";
const SUCCESS_BORDER = "#BBF7D0";

const INFO = "#0284C7";
const INFO_SUBTLE = "#E0F2FE";
const INFO_BORDER = "#BAE6FD";

export const colors = {
  // Semantic tokens
  brandPrimary: BRAND_PRIMARY,
  brandHover: BRAND_HOVER,
  brandActive: BRAND_ACTIVE,
  accent: ACCENT,
  accentSubtle: ACCENT_SUBTLE,
  accentBorder: ACCENT_BORDER,
  onAccent: "#FFFFFF",

  text: TEXT_PRIMARY,
  textPrimary: TEXT_PRIMARY,
  textSecondary: TEXT_SECONDARY,
  textMuted: TEXT_MUTED,
  textSubtle: TEXT_SUBTLE,

  pageBackground: PAGE_BACKGROUND,
  surface: SURFACE,
  surfaceSubtle: SURFACE_SUBTLE,
  surfaceMuted: SURFACE_MUTED,

  border: BORDER_DEFAULT,
  borderStrong: BORDER_STRONG,
  focusRing: "rgba(20, 184, 166, 0.35)",

  topbarBackground: TOPBAR_BACKGROUND,
  topbarSurface: TOPBAR_SURFACE,
  topbarSurfaceHover: TOPBAR_SURFACE_HOVER,
  sidebarBackground: SIDEBAR_BACKGROUND,
  sidebarDarker: SIDEBAR_DARKER,
  sidebarActive: SIDEBAR_ACTIVE,
  sidebarActiveBorder: SIDEBAR_ACTIVE_BORDER,
  chromeText: CHROME_TEXT,
  chromeTextMuted: CHROME_TEXT_MUTED,
  overlayBackdrop: OVERLAY_BACKDROP,

  danger: DANGER,
  dangerHover: DANGER_HOVER,
  dangerActive: DANGER_ACTIVE,
  dangerSubtle: DANGER_SUBTLE,
  dangerBorder: DANGER_BORDER,

  warning: WARNING,
  warningSubtle: WARNING_SUBTLE,
  warningBorder: WARNING_BORDER,

  success: SUCCESS,
  successSubtle: SUCCESS_SUBTLE,
  successBorder: SUCCESS_BORDER,

  info: INFO,
  infoSubtle: INFO_SUBTLE,
  infoBorder: INFO_BORDER,

  disabledBg: SURFACE_MUTED,
  disabledText: TEXT_SUBTLE,
  disabledBorder: BORDER_STRONG,

  tableHeader: SURFACE_SUBTLE,
  tableRowHover: "#F0FDFA",
  tableRowSelected: ACCENT_SUBTLE,
  tableRowSelectedBorder: BRAND_PRIMARY,

  // Backward-compatible aliases used by existing modules/components.
  primary: BRAND_PRIMARY,
  legacyPrimary: LEGACY_PRIMARY,
  secondary: TEXT_SECONDARY,
  background: PAGE_BACKGROUND,
  card: SURFACE,
};

export const spacing = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
};

export const radius = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  pill: 999,
};

export const shadows = {
  xs: "0 1px 2px rgba(15, 23, 42, 0.06)",
  sm: "0 2px 6px rgba(15, 23, 42, 0.08)",
  md: "0 10px 30px rgba(15, 23, 42, 0.08)",
  lg: "0 20px 60px rgba(15, 23, 42, 0.14)",
};

export const typography = {
  size: {
    xs: 12,
    sm: 13,
    base: 14,
    lg: 18,
    xl: 24,
    "2xl": 28,
  },
  weight: {
    regular: 400,
    medium: 600,
    bold: 700,
  },
  lineHeight: {
    compact: 1.3,
    form: 1.4,
    reading: 1.6,
  },
};

export const semantic = {
  accent: {
    base: colors.brandPrimary,
    hover: colors.brandHover,
    active: colors.brandActive,
    subtle: colors.accentSubtle,
    border: colors.accentBorder,
    on: colors.onAccent,
  },
  text: {
    default: colors.text,
    secondary: colors.textSecondary,
    muted: colors.textMuted,
    subtle: colors.textSubtle,
  },
  surface: {
    base: colors.surface,
    subtle: colors.surfaceSubtle,
    muted: colors.surfaceMuted,
    page: colors.pageBackground,
  },
  border: {
    base: colors.border,
    strong: colors.borderStrong,
    focusRing: colors.focusRing,
  },
  state: {
    success: colors.success,
    warning: colors.warning,
    danger: colors.danger,
    info: colors.info,
  },
  disabled: {
    background: colors.disabledBg,
    text: colors.disabledText,
    border: colors.disabledBorder,
  },
  chrome: {
    topbar: colors.topbarBackground,
    topbarSurface: colors.topbarSurface,
    topbarSurfaceHover: colors.topbarSurfaceHover,
    sidebar: colors.sidebarBackground,
    sidebarDarker: colors.sidebarDarker,
    sidebarActive: colors.sidebarActive,
    sidebarActiveBorder: colors.sidebarActiveBorder,
    text: colors.chromeText,
    textMuted: colors.chromeTextMuted,
  },
};

// Backward-compatible aliases used by existing modules.
export const fontSizes = {
  sm: typography.size.xs,
  md: typography.size.sm,
  lg: typography.size.base,
  xl: typography.size.lg,
};

const tokenSet = {
  colors,
  spacing,
  radius,
  shadows,
  fontSizes,
  typography,
  semantic,
};

export default tokenSet;
